import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaEdit, FaTrash, FaPlus, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { IoIosCloseCircleOutline } from 'react-icons/io';
import { showToast } from "../../Components/utils/ShowToast";
import ConfirmDialog from '../../Components/utils/ReuseConfirmDialog';
import Loader from '../../Components/utils/Loader';

const AdminCategories = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedMain, setExpandedMain] = useState({});
    const [expandedSub, setExpandedSub] = useState({});
    const [filterText, setFilterText] = useState('');

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('add'); // add | edit
    const [modalLevel, setModalLevel] = useState('main'); // main | sub | child
    const [modalData, setModalData] = useState({
        name: '', icon: '', scope: 'local', type: 'product', isB2B: false,
        mainId: '', subId: '', childId: ''
    });

    // Confirm dialog
    const [confirm, setConfirm] = useState({ show: false, title: '', message: '', onConfirm: null });

    const API = process.env.REACT_APP_Backend_Host_Name;

    // Category writes are admin-only on the server, so every mutating call has
    // to carry the admin token. Reads stay public.
    const adminAuth = () => ({
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
    });

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API}/api/v1/categories`);
            setCategories(res.data.categories);
        } catch (err) {
            console.error(err);
            showToast('Error fetching categories', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchCategories(); }, []);

    // Toggle expand
    const toggleMain = (id) => setExpandedMain(prev => ({ ...prev, [id]: !prev[id] }));
    const toggleSub = (id) => setExpandedSub(prev => ({ ...prev, [id]: !prev[id] }));

    // Open modal for add/edit
    const openAddModal = (level, mainId = '', subId = '') => {
        setModalMode('add');
        setModalLevel(level);
        setModalData({ name: '', icon: '', scope: 'local', type: 'product', mainId, subId, childId: '' });
        setShowModal(true);
    };

    const openEditModal = (level, data) => {
        setModalMode('edit');
        setModalLevel(level);
        setModalData({ ...data });
        setShowModal(true);
    };

    const closeModal = () => { setShowModal(false); setModalData({ name: '', icon: '', scope: 'local', type: 'product', isB2B: false, mainId: '', subId: '', childId: '' }); };

    // Handle submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!modalData.name.trim()) return showToast('Name is required', 'error');

        try {
            if (modalLevel === 'main') {
                if (modalMode === 'add') {
                    await axios.post(`${API}/api/v1/categories`, {
                        name: modalData.name, icon: modalData.icon,
                        scope: modalData.scope, type: modalData.type, isB2B: modalData.isB2B
                    }, adminAuth());
                    showToast('Main category added', 'success');
                } else {
                    await axios.put(`${API}/api/v1/categories/${modalData.mainId}`, {
                        name: modalData.name, icon: modalData.icon,
                        scope: modalData.scope, type: modalData.type, isB2B: modalData.isB2B
                    }, adminAuth());
                    showToast('Main category updated', 'success');
                }
            } else if (modalLevel === 'sub') {
                if (modalMode === 'add') {
                    await axios.post(`${API}/api/v1/categories/${modalData.mainId}/subcategories`, {
                        name: modalData.name, icon: modalData.icon
                    }, adminAuth());
                    showToast('Subcategory added', 'success');
                } else {
                    await axios.put(`${API}/api/v1/categories/${modalData.mainId}/subcategories/${modalData.subId}`, {
                        name: modalData.name, icon: modalData.icon
                    }, adminAuth());
                    showToast('Subcategory updated', 'success');
                }
            } else if (modalLevel === 'child') {
                if (modalMode === 'add') {
                    await axios.post(`${API}/api/v1/categories/${modalData.mainId}/subcategories/${modalData.subId}/children`, {
                        name: modalData.name
                    }, adminAuth());
                    showToast('Child category added', 'success');
                } else {
                    await axios.put(`${API}/api/v1/categories/${modalData.mainId}/subcategories/${modalData.subId}/children/${modalData.childId}`, {
                        name: modalData.name
                    }, adminAuth());
                    showToast('Child category updated', 'success');
                }
            }
            closeModal();
            fetchCategories();
        } catch (err) {
            showToast(err.response?.data?.message || 'Error saving category', 'error');
        }
    };

    // Handle delete
    const handleDelete = async (level, mainId, subId = '', childId = '') => {
        const labels = { main: 'main category', sub: 'subcategory', child: 'child category' };
        setConfirm({
            show: true,
            title: `Delete ${labels[level]}?`,
            message: `Are you sure you want to delete this ${labels[level]}? This action cannot be undone.`,
            onConfirm: async () => {
                try {
                    if (level === 'main') {
                        await axios.delete(`${API}/api/v1/categories/${mainId}`, adminAuth());
                    } else if (level === 'sub') {
                        await axios.delete(`${API}/api/v1/categories/${mainId}/subcategories/${subId}`, adminAuth());
                    } else {
                        await axios.delete(`${API}/api/v1/categories/${mainId}/subcategories/${subId}/children/${childId}`, adminAuth());
                    }
                    showToast(`${labels[level]} deleted`, 'success');
                    fetchCategories();
                } catch (err) {
                    showToast(err.response?.data?.message || 'Error deleting', 'error');
                }
                setConfirm({ show: false, title: '', message: '', onConfirm: null });
            }
        });
    };

    // Filter categories
    const filteredCategories = categories.filter(cat => {
        if (!filterText) return true;
        const search = filterText.toLowerCase();
        if (cat.name.toLowerCase().includes(search)) return true;
        return cat.subcategories?.some(sub =>
            sub.name.toLowerCase().includes(search) ||
            sub.children?.some(child => child.name.toLowerCase().includes(search))
        );
    });

    if (loading) return <Loader />;

    return (
        <div className="adminCategoriesDiv">
            <div className="catHeader">
                <h2>Categories Management</h2>
                <button className="addBtn" onClick={() => openAddModal('main')}>
                    <FaPlus /> Add Main Category
                </button>
            </div>

            <input
                type="text"
                className="filterInput"
                placeholder="Search categories..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
            />

            <div className="catSummary">
                <span>{categories.length} main categories</span>
                <span>{categories.reduce((s, c) => s + (c.subcategories?.length || 0), 0)} subcategories</span>
                <span>{categories.reduce((s, c) => s + c.subcategories?.reduce((s2, sub) => s2 + (sub.children?.length || 0), 0), 0)} child categories</span>
            </div>

            <div className="catTree">
                {filteredCategories.map(cat => (
                    <div key={cat._id} className="catMainCard">
                        {/* Main Category Row */}
                        <div className="catMainRow" onClick={() => toggleMain(cat._id)}>
                            <div className="catMainLeft">
                                <span className="expandIcon">
                                    {expandedMain[cat._id] ? <FaChevronDown /> : <FaChevronRight />}
                                </span>
                                {cat.icon && <span className="catIcon">{cat.icon}</span>}
                                <span className="catName">{cat.name}</span>
                                <span className="catBadge scopeBadge">{cat.scope}</span>
                                <span className="catBadge typeBadge">{cat.type}</span>
                                {cat.isB2B && <span className="catBadge b2bBadge">B2B</span>}
                                <span className="catCount">{cat.subcategories?.length || 0} sub</span>
                            </div>
                            <div className="catActions" onClick={(e) => e.stopPropagation()}>
                                <button className="iconBtn addBtn" title="Add subcategory" onClick={() => openAddModal('sub', cat._id)}>
                                    <FaPlus />
                                </button>
                                <button className="iconBtn editBtn" title="Edit" onClick={() => openEditModal('main', {
                                    name: cat.name, icon: cat.icon, scope: cat.scope, type: cat.type, isB2B: cat.isB2B || false, mainId: cat._id
                                })}>
                                    <FaEdit />
                                </button>
                                <button className="iconBtn deleteBtn" title="Delete" onClick={() => handleDelete('main', cat._id)}>
                                    <FaTrash />
                                </button>
                            </div>
                        </div>

                        {/* Subcategories */}
                        {expandedMain[cat._id] && (
                            <div className="catSubList">
                                {cat.subcategories?.map(sub => (
                                    <div key={sub._id} className="catSubCard">
                                        <div className="catSubRow" onClick={() => toggleSub(sub._id)}>
                                            <div className="catSubLeft">
                                                <span className="expandIcon small">
                                                    {sub.children?.length > 0 ? (
                                                        expandedSub[sub._id] ? <FaChevronDown /> : <FaChevronRight />
                                                    ) : <span className="dotIcon">•</span>}
                                                </span>
                                                {sub.icon && <span className="catIcon">{sub.icon}</span>}
                                                <span className="catName">{sub.name}</span>
                                                {sub.children?.length > 0 && (
                                                    <span className="catCount">{sub.children.length} children</span>
                                                )}
                                            </div>
                                            <div className="catActions" onClick={(e) => e.stopPropagation()}>
                                                <button className="iconBtn addBtn" title="Add child" onClick={() => openAddModal('child', cat._id, sub._id)}>
                                                    <FaPlus />
                                                </button>
                                                <button className="iconBtn editBtn" title="Edit" onClick={() => openEditModal('sub', {
                                                    name: sub.name, icon: sub.icon, mainId: cat._id, subId: sub._id
                                                })}>
                                                    <FaEdit />
                                                </button>
                                                <button className="iconBtn deleteBtn" title="Delete" onClick={() => handleDelete('sub', cat._id, sub._id)}>
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Child Categories */}
                                        {expandedSub[sub._id] && sub.children?.length > 0 && (
                                            <div className="catChildList">
                                                {sub.children.map(child => (
                                                    <div key={child._id} className="catChildRow">
                                                        <div className="catChildLeft">
                                                            <span className="dotIcon">◦</span>
                                                            <span className="catName">{child.name}</span>
                                                        </div>
                                                        <div className="catActions" onClick={(e) => e.stopPropagation()}>
                                                            <button className="iconBtn editBtn" title="Edit" onClick={() => openEditModal('child', {
                                                                name: child.name, mainId: cat._id, subId: sub._id, childId: child._id
                                                            })}>
                                                                <FaEdit />
                                                            </button>
                                                            <button className="iconBtn deleteBtn" title="Delete" onClick={() => handleDelete('child', cat._id, sub._id, child._id)}>
                                                                <FaTrash />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="catModalOverlay" onClick={closeModal}>
                    <div className="catModal" onClick={(e) => e.stopPropagation()}>
                        <div className="catModalHeader">
                            <h3>{modalMode === 'add' ? 'Add' : 'Edit'} {modalLevel === 'main' ? 'Main Category' : modalLevel === 'sub' ? 'Subcategory' : 'Child Category'}</h3>
                            <button className="closeBtn" onClick={closeModal}><IoIosCloseCircleOutline /></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="formGroup">
                                <label>Name</label>
                                <input
                                    type="text"
                                    value={modalData.name}
                                    onChange={(e) => setModalData(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="Category name"
                                    autoFocus
                                />
                            </div>

                            {modalLevel !== 'child' && (
                                <div className="formGroup">
                                    <label>Icon (emoji)</label>
                                    <input
                                        type="text"
                                        value={modalData.icon}
                                        onChange={(e) => setModalData(prev => ({ ...prev, icon: e.target.value }))}
                                        placeholder="e.g. 🍰"
                                    />
                                </div>
                            )}

                            {modalLevel === 'main' && (
                                <>
                                    <div className="formGroup">
                                        <label>Scope</label>
                                        <select
                                            value={modalData.scope}
                                            onChange={(e) => setModalData(prev => ({ ...prev, scope: e.target.value }))}
                                        >
                                            <option value="local">Local</option>
                                            <option value="international">International</option>
                                        </select>
                                    </div>
                                    <div className="formGroup">
                                        <label>Type</label>
                                        <select
                                            value={modalData.type}
                                            onChange={(e) => setModalData(prev => ({ ...prev, type: e.target.value }))}
                                        >
                                            <option value="product">Product</option>
                                            <option value="service">Service</option>
                                        </select>
                                    </div>
                                    <div className="formGroup" style={{ flexDirection: 'row', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                                        <input
                                            type="checkbox"
                                            id="isB2B"
                                            checked={modalData.isB2B}
                                            onChange={(e) => setModalData(prev => ({ ...prev, isB2B: e.target.checked }))}
                                            style={{ width: '18px', height: '18px' }}
                                        />
                                        <label htmlFor="isB2B" style={{ cursor: 'pointer', fontWeight: 600, color: '#1a56db' }}>B2B Category</label>
                                    </div>
                                </>
                            )}

                            <div className="formActions">
                                <button type="button" className="cancelBtn" onClick={closeModal}>Cancel</button>
                                <button type="submit" className="submitBtn">
                                    {modalMode === 'add' ? 'Add' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={confirm.show}
                title={confirm.title}
                message={confirm.message}
                onConfirm={confirm.onConfirm}
                onCancel={() => setConfirm({ show: false, title: '', message: '', onConfirm: null })}
            />
        </div>
    );
};

export default AdminCategories;